import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/manga";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [query, setQuery] = useState("");
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    // 简易站内搜索：跳转到冬物语精选页并带关键词
    window.REACT_APP_NAVIGATE(`/category/dongwuyu?q=${encodeURIComponent(q)}`);
  };

  const navLink = (to: string, label: string) => (
    <Link
      to={to}
      className="whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-200/60 hover:text-foreground-950"
    >
      {label}
    </Link>
  );

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-background-200/60 bg-background-950/85 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5" aria-label="吹雪漫画网 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
            <i className="ri-snowy-line text-xl"></i>
          </span>
          <span className="font-heading text-lg font-bold tracking-wide text-foreground-950">
            吹雪漫画网
          </span>
        </Link>

        {/* 桌面导航 */}
        <div className="hidden items-center gap-1 lg:flex">
          {navLink("/", "首页")}
          <div className="relative">
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-200/60 hover:text-foreground-950"
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-56 rounded-lg border border-background-200/70 bg-background-100 p-2 shadow-soft">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-200/70 hover:text-foreground-950"
                  >
                    <i className={`${c.icon} text-primary-500`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {navLink("/about", "关于我们")}
          {navLink("/faq", "常见问题")}
          {navLink("/contact", "联系我们")}
        </div>

        {/* 右侧搜索 + CTA */}
        <div className="flex items-center gap-3">
          <form
            onSubmit={submitSearch}
            className="hidden items-center gap-2 rounded-full border border-background-300/70 bg-background-100/70 px-3 py-1.5 md:flex"
          >
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜一部冬日漫画…"
              className="w-36 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
            />
          </form>
          <Link
            to="/category/xuejing"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            立即开读
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-950 transition-colors hover:bg-background-200/60 lg:hidden"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </nav>

      {/* 移动端菜单 */}
      {mobileOpen && (
        <div className="border-t border-background-200/60 bg-background-950/95 px-4 pb-5 pt-3 lg:hidden">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2 rounded-full border border-background-300/70 bg-background-100 px-3 py-2">
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜一部冬日漫画…"
              className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
            />
          </form>
          <div className="flex flex-col gap-1">
            {[
              { to: "/", label: "首页" },
              { to: "/about", label: "关于我们" },
              { to: "/faq", label: "常见问题" },
              { to: "/contact", label: "联系我们" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-200/60">
                {l.label}
              </Link>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 border-t border-background-200/60 pt-3">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="whitespace-nowrap rounded-full border border-background-300/70 bg-background-100 px-3 py-1.5 text-xs text-foreground-700 hover:text-foreground-950"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}